/**
 * /campaigns/:id/metrics — Story 11.7.
 *
 * UI de métricas comparativas A/B/C/N, consumindo
 * `GET /admin/campaigns/:campaignId/variant-metrics` (Story 11.6).
 *
 * Surface:
 *   - Header: voltar + nome da campanha
 *   - Filtro de janela: presets 7d/14d/30d + custom (from/to date inputs)
 *   - Cards de resumo (3): impressions / responses / conversions totais
 *   - Tabela comparativa (VariantMetricsTable) com champion highlight + low volume warning
 *   - Estados: loading skeleton / 2 empty states / error retry
 */

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError, apiClient } from '@/lib/api';
import { formatRate, toDateInputValue } from '@/lib/metrics-format';
import {
  VariantMetricsTable,
  type VariantMetricRow,
} from '@/components/campaigns/VariantMetricsTable';

interface CampaignLite {
  id: string;
  name: string;
}

interface MetricsResponse {
  campaign_id: string;
  window: { from: string; to: string };
  total_active_variants: number;
  variants: VariantMetricRow[];
}

type Preset = '7d' | '14d' | '30d' | 'custom';

const MAX_WINDOW_DAYS = 90;
const DAY_MS = 24 * 60 * 60 * 1000;

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { code?: string; message?: string } };
    if (body?.error?.message) return body.error.message;
    return `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

function presetToWindow(preset: Preset): { from: string; to: string } {
  const to = new Date();
  const days = preset === '14d' ? 14 : preset === '30d' ? 30 : 7;
  const from = new Date(to.getTime() - days * DAY_MS);
  return { from: from.toISOString(), to: to.toISOString() };
}

export function CampaignVariantMetricsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [preset, setPreset] = useState<Preset>('7d');
  const [customFrom, setCustomFrom] = useState<string>(
    toDateInputValue(new Date(Date.now() - 7 * DAY_MS)),
  );
  const [customTo, setCustomTo] = useState<string>(toDateInputValue(new Date()));

  const windowValidation = useMemo(() => {
    if (preset !== 'custom') return null;
    const fromMs = new Date(`${customFrom}T00:00:00`).getTime();
    const toMs = new Date(`${customTo}T23:59:59`).getTime();
    if (Number.isNaN(fromMs) || Number.isNaN(toMs)) return 'Datas inválidas.';
    if (fromMs > toMs) return 'A data inicial precisa ser anterior ou igual à data final.';
    const days = (toMs - fromMs) / DAY_MS;
    if (days > MAX_WINDOW_DAYS) return `Janela máxima de ${MAX_WINDOW_DAYS} dias.`;
    return null;
  }, [preset, customFrom, customTo]);

  const window = useMemo(() => {
    if (preset !== 'custom') return presetToWindow(preset);
    // Janela inválida → fallback pro último 7d só pra manter queryKey estável; o
    // fetch é gated por `windowValidation === null` em metricsQuery.enabled.
    if (windowValidation !== null) return presetToWindow('7d');
    const fromDate = new Date(`${customFrom}T00:00:00`);
    const toDate = new Date(`${customTo}T23:59:59.999`);
    return { from: fromDate.toISOString(), to: toDate.toISOString() };
  }, [preset, customFrom, customTo, windowValidation]);

  const campaignQuery = useQuery({
    queryKey: ['admin', 'campaigns', id],
    queryFn: () => apiClient.get<CampaignLite>(`/admin/campaigns/${id!}`),
    enabled: !!id,
  });

  const metricsQuery = useQuery({
    queryKey: ['admin', 'campaigns', id, 'metrics', window.from, window.to],
    queryFn: () =>
      apiClient.get<MetricsResponse>(
        `/admin/campaigns/${id!}/variant-metrics?from=${encodeURIComponent(window.from)}&to=${encodeURIComponent(window.to)}`,
      ),
    enabled: !!id && windowValidation === null,
  });

  const totals = useMemo(() => {
    if (!metricsQuery.data) return null;
    const variants = metricsQuery.data.variants;
    const impressions = variants.reduce((acc, v) => acc + v.impressions, 0);
    const responses = variants.reduce((acc, v) => acc + v.responses, 0);
    const conversions = variants.reduce((acc, v) => acc + v.conversions, 0);
    return {
      impressions,
      responses,
      conversions,
      responseRate: impressions > 0 ? responses / impressions : null,
      conversionRate: impressions > 0 ? conversions / impressions : null,
    };
  }, [metricsQuery.data]);

  // Defensive early return AFTER all hooks (Rules of Hooks).
  if (!id) return null;

  const suppressChampion =
    (metricsQuery.data?.total_active_variants ?? 0) <= 1 || (totals?.impressions ?? 0) === 0;

  const hasZeroVariants = metricsQuery.data && metricsQuery.data.variants.length === 0;
  const hasZeroImpressions =
    metricsQuery.data && metricsQuery.data.variants.length > 0 && (totals?.impressions ?? 0) === 0;

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/campaigns/${id}/variants`)}>
          <ArrowLeft aria-hidden className="mr-1" /> Voltar pra Variantes
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Métricas comparativas{campaignQuery.data ? ` — ${campaignQuery.data.name}` : ''}
            </h1>
            <p className="text-sm text-muted-foreground">
              Performance A/B/C/N por variante na janela selecionada. Champion = maior
              <code>conversion_rate</code>. Variantes com &lt;30 impressions têm aviso de dados
              insuficientes.
            </p>
          </div>
          {metricsQuery.isFetching && !metricsQuery.isLoading && (
            <span
              data-testid="metrics-refetching"
              className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
            >
              <RefreshCw className="h-3 w-3 animate-spin" aria-hidden />
              atualizando…
            </span>
          )}
        </div>
      </div>

      {/* Filtro de janela */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Janela</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div
            className="flex flex-wrap items-center gap-2"
            role="radiogroup"
            aria-label="Preset de janela"
          >
            {(['7d', '14d', '30d', 'custom'] as const).map((p) => (
              <Button
                key={p}
                type="button"
                variant={preset === p ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPreset(p)}
                aria-pressed={preset === p}
                data-testid={`preset-${p}`}
              >
                {p === 'custom' ? 'Custom' : `Últimos ${p}`}
              </Button>
            ))}
          </div>
          {preset === 'custom' && (
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label htmlFor="custom-from">De</Label>
                <Input
                  id="custom-from"
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="w-44"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="custom-to">Até</Label>
                <Input
                  id="custom-to"
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="w-44"
                />
              </div>
            </div>
          )}
          {windowValidation && (
            <p data-testid="window-validation-error" className="text-xs text-destructive">
              {windowValidation}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Resumo agregado */}
      {totals && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-xs uppercase text-muted-foreground">
                Impressions totais
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold">{totals.impressions}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-xs uppercase text-muted-foreground">Responses</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold">
                {totals.responses}{' '}
                <span className="text-sm font-normal text-muted-foreground">
                  ({formatRate(totals.responseRate)})
                </span>
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-xs uppercase text-muted-foreground">Conversions</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold">
                {totals.conversions}{' '}
                <span className="text-sm font-normal text-muted-foreground">
                  ({formatRate(totals.conversionRate)})
                </span>
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabela ou states */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Variantes</CardTitle>
        </CardHeader>
        <CardContent>
          {metricsQuery.isLoading ? (
            <div data-testid="metrics-loading" className="space-y-2">
              <div className="h-9 animate-pulse rounded bg-muted" />
              <div className="h-9 animate-pulse rounded bg-muted" />
              <div className="h-9 animate-pulse rounded bg-muted" />
            </div>
          ) : metricsQuery.error ? (
            <div
              role="alert"
              data-testid="metrics-error"
              className="space-y-2 rounded border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
            >
              <div>{humanizeError(metricsQuery.error, 'Falha ao carregar métricas.')}</div>
              <Button variant="outline" size="sm" onClick={() => void metricsQuery.refetch()}>
                Tentar novamente
              </Button>
            </div>
          ) : hasZeroVariants ? (
            <div
              data-testid="metrics-empty-no-variants"
              className="space-y-3 rounded-md border border-dashed bg-muted/30 px-3 py-8 text-center"
            >
              <p className="text-sm text-muted-foreground">
                Nenhuma variante configurada nessa campanha. Crie variantes na tab de Variantes pra
                começar a coletar métricas.
              </p>
              <Button onClick={() => navigate(`/campaigns/${id}/variants`)}>
                Ir pra Variantes
              </Button>
            </div>
          ) : hasZeroImpressions ? (
            <div
              data-testid="metrics-empty-no-data"
              className="space-y-2 rounded-md border border-dashed bg-muted/30 px-3 py-8 text-center"
            >
              <p className="text-sm text-muted-foreground">
                Sem disparos no período selecionado — variantes existem mas ainda não foram usadas.
                Tente expandir a janela ou aguarde mais disparos.
              </p>
            </div>
          ) : (
            <VariantMetricsTable
              variants={metricsQuery.data?.variants ?? []}
              suppressChampion={suppressChampion}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default CampaignVariantMetricsPage;
